/**
 * Shared API client for the admin dashboard.
 *
 * Mirrors the customer SPA's auth model (cookie-primary, in-memory
 * bearer fallback for iOS Safari ITP) but is a SEPARATE axios
 * instance — sharing one between two SPAs across subdomains is
 * messy because they'd race on the same module-scoped state inside
 * an iframe or shared worker scenario. Two independent clients with
 * the same behaviour is cleaner.
 *
 * Auth flow:
 *   1. SPA hits ``GET /auth/me`` on mount.
 *   2. If 401, redirect to the customer login at ``app.lenavio.com``
 *      with a return-to query param so the user lands back here
 *      after logging in.
 *   3. If 200 BUT (user.role !== SUPER_ADMIN || !user.org.is_internal),
 *      hard-redirect to ``app.lenavio.com`` — they're a customer,
 *      not an internal user. Show no error chrome here; the SPA
 *      should never reveal that this dashboard exists.
 *   4. Otherwise render the admin shell.
 */
import axios, { AxiosError } from "axios";
import { API_BASE_URL } from "@/lib/constants";

/** Module-scope in-memory bearer fallback for iOS Safari ITP — same
 *  pattern as the customer SPA. NOT stored in localStorage. */
let _bearer: string | null = null;

export function setBearer(token: string | null): void {
  _bearer = token;
}

export function getBearer(): string | null {
  return _bearer;
}

/** Module-scope CSRF token mirror — populated lazily on first
 *  state-changing request. */
let _csrf: string | null = null;
let _csrfInFlight: Promise<string | null> | null = null;

async function ensureCsrfToken(): Promise<string | null> {
  if (_csrf) return _csrf;
  if (_csrfInFlight) return _csrfInFlight;
  _csrfInFlight = (async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/auth/csrf`, {
        withCredentials: true,
      });
      const tok = res.data?.csrf_token as string | undefined;
      if (tok) _csrf = tok;
      return _csrf;
    } catch {
      return null;
    } finally {
      setTimeout(() => {
        _csrfInFlight = null;
      }, 0);
    }
  })();
  return _csrfInFlight;
}

export const client = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Request interceptor — bearer + CSRF
client.interceptors.request.use(async (config) => {
  const bearer = getBearer();
  if (bearer && !config.headers["Authorization"]) {
    config.headers["Authorization"] = `Bearer ${bearer}`;
  }
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrf = await ensureCsrfToken();
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

/**
 * 401 handler. The admin dashboard does NOT silent-refresh — if you
 * lost the session, you need to log back in via the customer SPA
 * (where the login form lives). On 401 we redirect to the customer
 * login. No retry loop; that just delays the same redirect.
 */
client.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    if (err.response?.status === 401) {
      // Send the user to the customer login. After they log in,
      // they'll land back at /, which the customer SPA routes
      // normally. They'd need to manually navigate back to the
      // admin subdomain — but that's the right trade for keeping
      // login logic in one place.
      try {
        const back = encodeURIComponent(window.location.href);
        window.location.assign(
          `${
            import.meta.env.VITE_CUSTOMER_APP_URL ||
            "https://app.lenavio.com"
          }/login?return_to=${back}`,
        );
      } catch {
        /* navigation failure — let the rejection bubble */
      }
    }
    return Promise.reject(err);
  },
);

export default client;
